import React from "react";
import { ChevronRight, ExternalLink, Globe, Pill, Book, ShieldAlert } from "lucide-react";
import { motion } from "motion/react";

const WEBSITES = [
  {
    name: "Medscape Reference",
    description: "أشمل مرجع طبي مجاني للأمراض والعلاجات والأخبار الطبية (بديل متكامل للـ BNF)",
    url: "https://reference.medscape.com/",
    icon: Globe,
    color: "bg-blue-500",
    badge: "مرجع شامل"
  },
  {
    name: "Drugs.com (Pro)",
    description: "قاعدة بيانات ضخمة للأدوية، التداخلات الدوائية (Interactions)، والآثار الجانبية",
    url: "https://www.drugs.com/pro/",
    icon: Pill,
    color: "bg-red-500",
    badge: "أدوية وتداخلات"
  },
  {
    name: "DermNet NZ",
    description: "الموسوعة العالمية المفتوحة الأولى عالمياً في طب الأمراض الجلدية",
    url: "https://dermnetnz.org/",
    icon: Book,
    color: "bg-emerald-500",
    badge: "طب الجلدية"
  },
  {
    name: "emc (UK Medicines)",
    description: "المرجع البريطاني المفتوح وبديل BNF المباشر، يحتوي على جميع معلومات الأدوية في بريطانيا",
    url: "https://www.medicines.org.uk/emc",
    icon: Pill,
    color: "bg-indigo-500",
    badge: "بديل BNF"
  }
];

export default function ImportantWebsitesViewer({ onBack }: { onBack: () => void }) {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col pt-16 md:pt-24 pb-8 md:pb-12 px-4 relative overflow-hidden" dir="rtl">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-[50vh] md:h-96 bg-slate-900 rounded-b-[4rem] z-0" />
      
      <div className="max-w-4xl mx-auto w-full relative z-10 flex-1 flex flex-col">
        <button 
          onClick={onBack}
          className="self-end mb-8 bg-white/10 hover:bg-white/20 text-white rounded-full py-2 px-5 flex items-center gap-2 transition-all border border-white/20 backdrop-blur-sm"
        >
          <ChevronRight className="w-4 h-4" />
          <span className="text-sm font-bold">العودة للبوابة</span>
        </button>

        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center p-4 bg-white/10 rounded-full mb-4 backdrop-blur-sm border border-white/20">
            <Globe className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-4 tracking-tight">مواقع طبية مهمة</h1>
          <p className="text-slate-300 max-w-2xl mx-auto text-lg leading-relaxed">
            مجموعة من أهم المراجع الطبية العالمية المجانية المفتوحة، مختارة بعناية لدعم القرار السريري السريع.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          {WEBSITES.map((site, idx) => (
            <motion.div
              key={site.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              onClick={() => window.open(site.url, '_blank')}
              className="bg-white rounded-3xl p-6 shadow-xl border border-slate-100 hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer group relative overflow-hidden flex flex-col"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${site.color} bg-opacity-10 group-hover:scale-110 transition-transform duration-300`}>
                  <site.icon className={`w-7 h-7 ${site.color.replace('bg-', 'text-')}`} />
                </div>
                <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-xs font-bold border border-slate-200">
                  {site.badge}
                </span>
              </div>
              
              <h3 className="text-xl font-bold text-slate-800 mb-2 group-hover:text-indigo-600 transition-colors">
                {site.name}
              </h3>
              <p className="text-slate-500 text-sm leading-relaxed mb-6 flex-1">
                {site.description}
              </p>
              
              <div className="flex items-center justify-end text-indigo-600 font-bold text-sm gap-2 mt-auto border-t border-slate-50 pt-4">
                <span>زيارة الموقع</span>
                <ExternalLink className="w-4 h-4" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
